/**
 * primary/secondary/ghost: ação comum. danger/success/warning: preenchidos, para
 * decisão (excluir, aprovar, devolver). `*-outline`: mesma intenção, sem peso visual,
 * para a ação que não deve competir com a principal (ex.: Reprovar ao lado de Aprovar).
 * info: ação secundária de navegação/consulta (ex.: "Preencher análise jurídica",
 * "Ver parecer") — o tom que a tela já usava como `bg-info-soft border-info-line`.
 */
export type ButtonVariant =
  | 'primary' | 'secondary' | 'ghost' | 'info'
  | 'danger' | 'success' | 'warning'
  | 'danger-outline' | 'success-outline' | 'warning-outline';
export type ButtonSize = 'sm' | 'md';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-primary text-primary-fg hover:bg-primary-hover focus-visible:ring-primary/40',
  secondary: 'border-line-strong bg-surface text-fg hover:bg-surface-muted focus-visible:ring-primary/40',
  danger: 'border-transparent bg-danger text-primary-fg hover:bg-danger-hover focus-visible:ring-danger/40',
  ghost: 'border-transparent bg-transparent text-fg-muted hover:bg-surface-muted hover:text-fg focus-visible:ring-primary/40',
  success: 'border-transparent bg-success text-primary-fg hover:bg-success-hover focus-visible:ring-success/40',
  warning: 'border-transparent bg-warning text-primary-fg hover:bg-warning-hover focus-visible:ring-warning/40',
  info: 'border-info-line bg-info-soft text-info-fg hover:bg-info-hover focus-visible:ring-info/40',
  'danger-outline': 'border-danger bg-transparent text-danger-text hover:bg-danger-soft focus-visible:ring-danger/40',
  'success-outline': 'border-success bg-transparent text-success-text hover:bg-success-soft focus-visible:ring-success/40',
  'warning-outline': 'border-warning bg-transparent text-warning-text hover:bg-warning-soft focus-visible:ring-warning/40',
};

// Altura fixa por tamanho: todo botão da mesma linha tem a mesma altura.
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-control-sm px-3 text-xs',
  md: 'h-control px-4 text-sm',
};

/**
 * Classes do botão, para elementos que não são `<button>` mas precisam do mesmo
 * visual — tipicamente `<Link>` de navegação: `className={buttonClassName({ variant: 'secondary' })}`.
 */
// Botão só de ícone: quadrado, sem padding horizontal. Sem texto, então precisa de `aria-label`.
const SIZE_ICON: Record<ButtonSize, string> = {
  sm: 'h-control-sm w-8 p-0 text-xs',
  md: 'h-control w-10 p-0 text-sm',
};

export const buttonClassName = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  pill = false,
  iconOnly = false,
}: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean; pill?: boolean; iconOnly?: boolean } = {}): string =>
  `inline-flex items-center justify-center gap-2 ${pill ? 'rounded-full' : 'rounded-control'} border font-semibold transition focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${VARIANT[variant]} ${iconOnly ? SIZE_ICON[size] : SIZE[size]} ${fullWidth ? 'w-full' : ''}`;
